import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import Groups2Icon from '@mui/icons-material/Groups2';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { useLocation, useNavigate } from 'react-router-dom';

import { trackFrontendEvent } from '../api/events';
import { selectSignupPlan } from '../api/subscription';
import { useAuth } from '../context/authContext';
import { useSubscription } from '../context/SubscriptionContext';
import { formatApiError } from '../utils/formatApiError';
import ActionButton from '../components/common/ActionButton';

type PlanIntent = 'free' | 'starter' | 'team' | 'business' | 'enterprise';

const PLAN_CARD_COPY: Record<
  PlanIntent,
  {
    title: string;
    subtitle: string;
    cta: string;
    features: string[];
    icon: React.ReactNode;
    tone?: 'primary' | 'neutral';
  }
> = {
  free: {
    title: 'Free',
    subtitle: 'Explore intelligent test selection with low-volume usage.',
    cta: 'Start Free',
    features: [
      '10 selections / month',
      'Unlimited logical services',
      '$0/month',
      '7-day recent history',
      'Functional selection',
      'API access, rate-limited',
    ],
    icon: <AutoAwesomeIcon sx={{ color: '#39FF14' }} />,
    tone: 'primary',
  },
  starter: {
    title: 'Starter',
    subtitle: 'For growing teams adopting Quantik Mind in regular delivery workflows.',
    cta: 'Continue with Starter',
    features: [
      '100K test selections / month',
      '5 services',
      '$400/month',
      '$4,000/year',
      'Functional + Chaos selection',
    ],
    icon: <Groups2Icon sx={{ color: '#9CDCFE' }} />,
  },
  team: {
    title: 'Team',
    subtitle: 'For teams using Quantik Mind in active CI/CD workflows.',
    cta: 'Continue with Team',
    features: [
      '500K test selections / month',
      '10 services',
      '$1,200/month',
      '$12,000/year',
      'Functional + Chaos selection',
      'Full decision history',
    ],
    icon: <Groups2Icon sx={{ color: '#9CDCFE' }} />,
  },
  business: {
    title: 'Business',
    subtitle: 'For larger delivery organizations managing several active product surfaces.',
    cta: 'Continue with Business',
    features: [
      '3M test selections / month',
      '30 services',
      '$3,500/month',
      '$35,000/year',
      'Functional + Chaos selection',
    ],
    icon: <Groups2Icon sx={{ color: '#F7D774' }} />,
  },
  enterprise: {
    title: 'Enterprise',
    subtitle: 'For large distributed systems that need flexible commercial terms and scale.',
    cta: 'Talk to us about Enterprise',
    features: [
      'Custom included usage',
      'Unlimited services',
      'Enterprise support',
      'Contracted overage handling',
      'Operational onboarding',
    ],
    icon: <Groups2Icon sx={{ color: '#F39DFF' }} />,
  },
};

const PlanCard: React.FC<{
  plan: PlanIntent;
  selected: boolean;
  loading: boolean;
  onClick: (plan: PlanIntent) => void;
}> = ({ plan, selected, loading, onClick }) => {
  const copy = PLAN_CARD_COPY[plan];
  const scrollToComparison = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    document.getElementById('plan-comparison')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  return (
    <Card
      className="enterprise-card"
      sx={{
        height: '100%',
        borderRadius: 4,
        borderColor: selected ? 'rgba(57,255,20,0.28)' : 'rgba(255,255,255,0.08)',
        background:
          selected
            ? 'linear-gradient(180deg, rgba(17, 24, 18, 0.98) 0%, rgba(10, 14, 20, 0.99) 100%)'
            : 'linear-gradient(180deg, rgba(15, 21, 30, 0.96) 0%, rgba(10, 14, 20, 0.98) 100%)',
        boxShadow: selected
          ? '0 20px 50px rgba(57, 255, 20, 0.12)'
          : '0 16px 38px rgba(0, 0, 0, 0.22)',
      }}
    >
      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
        <Stack spacing={2.5} sx={{ height: '100%' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Stack spacing={0.75}>
              <Typography variant="h5" fontWeight={750}>
                {copy.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                {copy.subtitle}
              </Typography>
            </Stack>
            <Box>{copy.icon}</Box>
          </Stack>

          <Stack spacing={1}>
            {copy.features.map((feature) => (
              <Typography key={feature} variant="body2" sx={{ color: 'rgba(255,255,255,0.82)' }}>
                {feature}
              </Typography>
            ))}
          </Stack>

          <Box sx={{ flexGrow: 1 }} />

          <ActionButton
            onClick={() => onClick(plan)}
            disabled={loading}
            sx={{
              alignSelf: 'flex-start',
            }}
          >
            {loading ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : copy.cta}
          </ActionButton>
          <Link
            href="#plan-comparison"
            onClick={scrollToComparison}
            underline="hover"
            sx={{
              alignSelf: 'flex-start',
              color: 'rgba(255,255,255,0.62)',
              fontSize: '0.82rem',
            }}
          >
            Compare all plans
          </Link>
        </Stack>
      </CardContent>
    </Card>
  );
};

const TeamPlaceholderCard: React.FC<{
  busy: boolean;
  isInternalAdmin: boolean;
  planLabel?: string;
  onContinueFree: () => void;
  onBack: () => void;
}> = ({ busy, isInternalAdmin, planLabel = 'Paid plan', onContinueFree, onBack }) => (
  <Card
    className="enterprise-card"
    sx={{
      borderRadius: 4,
      background:
        'linear-gradient(180deg, rgba(15, 21, 30, 0.96) 0%, rgba(10, 14, 20, 0.98) 100%)',
      borderColor: 'rgba(255,255,255,0.08)',
    }}
  >
    <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
      <Stack spacing={2.25}>
        <Chip
          label="Team setup"
          size="small"
          sx={{
            alignSelf: 'flex-start',
            color: '#E6EDF3',
            bgcolor: 'rgba(255,255,255,0.06)',
          }}
        />
        <Box>
          <Typography variant="h5" fontWeight={750} sx={{ mb: 1 }}>
            {planLabel} checkout is not connected yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.75 }}>
            Your account has been created. Team self-service payment will be
            available through checkout. For now, contact us or continue on Free.
          </Typography>
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />

        {isInternalAdmin && (
          <Alert
            severity="info"
            sx={{
              bgcolor: 'rgba(0, 180, 255, 0.08)',
              border: '1px solid rgba(0, 180, 255, 0.18)',
            }}
          >
            <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
              Internal testing: use Settings → License &amp; Usage → Test organization
              plan, or switch this organization to Team from the internal plan
              control.
            </Typography>
          </Alert>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
          <ActionButton
            href="mailto:info@quantikmind.com?subject=Quantik%20Mind%20Team%20Activation"
            startIcon={<MailOutlineIcon />}
          >
            Contact us
          </ActionButton>
          <ActionButton
            onClick={onContinueFree}
            disabled={busy}
          >
            {busy ? 'Preparing Free...' : 'Continue on Free'}
          </ActionButton>
          <ActionButton
            onClick={onBack}
          >
            Back to plans
          </ActionButton>
        </Stack>
      </Stack>
    </CardContent>
  </Card>
);

export default function PlanSelectionPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { authData } = useAuth();
  const { refreshSubscription } = useSubscription();
  const [loadingPlan, setLoadingPlan] = useState<PlanIntent | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [placeholderPlan, setPlaceholderPlan] = useState<Exclude<PlanIntent, 'free'> | null>(null);

  const planIntent = useMemo(() => {
    const raw = new URLSearchParams(location.search).get('plan');
    if (
      raw === 'starter' ||
      raw === 'team' ||
      raw === 'business' ||
      raw === 'enterprise'
    ) {
      return raw;
    }
    return 'free';
  }, [location.search]);

  const handleSelectPlan = async (plan: PlanIntent) => {
    setLoadingPlan(plan);
    setError(null);

    try {
      const response = await selectSignupPlan(plan);
      await refreshSubscription();

      if (response.status === 'selected' && plan === 'free') {
        navigate('/onboarding');
        return;
      }

      if (response.status === 'checkout_required') {
        setPlaceholderPlan(plan === 'free' ? null : plan);
        void trackFrontendEvent('upgrade_page_viewed', {
          selected_plan: plan,
          source: 'post_signup_plan_selection',
        }).catch(() => undefined);
        return;
      }

      navigate('/onboarding');
    } catch (err) {
      setError(formatApiError(err, 'Unable to continue with the selected plan.'));
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <Box
      className="page-transition"
      sx={{
        width: '100%',
        minWidth: 0,
        maxWidth: 1080,
        mx: 'auto',
        py: { xs: 2, md: 4 },
      }}
    >
      <Stack spacing={3}>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 760, letterSpacing: '-0.03em', mb: 1 }}>
            How do you want to start?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 720 }}>
            Start exploring for free, or set up Quantik Mind for your team.
          </Typography>
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{
              bgcolor: 'rgba(255, 190, 92, 0.08)',
              border: '1px solid rgba(255, 190, 92, 0.18)',
            }}
          >
            {error}
          </Alert>
        )}

        {placeholderPlan ? (
          <TeamPlaceholderCard
            busy={loadingPlan === 'free'}
            isInternalAdmin={!!authData?.isInternalAdmin}
            planLabel={PLAN_CARD_COPY[placeholderPlan].title}
            onContinueFree={() => void handleSelectPlan('free')}
            onBack={() => setPlaceholderPlan(null)}
          />
        ) : (
          <Stack direction={{ xs: 'column', xl: 'row' }} spacing={2.5} flexWrap="wrap" useFlexGap>
            <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
              <PlanCard
                plan="free"
                selected={planIntent === 'free'}
                loading={loadingPlan === 'free'}
                onClick={(plan) => void handleSelectPlan(plan)}
              />
            </Box>
            <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
              <PlanCard
                plan="starter"
                selected={planIntent === 'starter'}
                loading={loadingPlan === 'starter'}
                onClick={(plan) => void handleSelectPlan(plan)}
              />
            </Box>
            <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
              <PlanCard
                plan="team"
                selected={planIntent === 'team'}
                loading={loadingPlan === 'team'}
                onClick={(plan) => void handleSelectPlan(plan)}
              />
            </Box>
            <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
              <PlanCard
                plan="business"
                selected={planIntent === 'business'}
                loading={loadingPlan === 'business'}
                onClick={(plan) => void handleSelectPlan(plan)}
              />
            </Box>
            <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
              <PlanCard
                plan="enterprise"
                selected={planIntent === 'enterprise'}
                loading={loadingPlan === 'enterprise'}
                onClick={(plan) => void handleSelectPlan(plan)}
              />
            </Box>
          </Stack>
        )}

        {!placeholderPlan && (
          <Card
            id="plan-comparison"
            className="enterprise-card"
            sx={{
              borderRadius: 3,
              borderColor: 'rgba(255,255,255,0.08)',
              scrollMarginTop: 96,
            }}
          >
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="h6" fontWeight={750}>
                    Plan comparison
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Compare the onboarding options before choosing a plan.
                  </Typography>
                </Box>
                <TableContainer>
                  <Table size="small" aria-label="Plan comparison">
                    <TableHead>
                      <TableRow>
                        <TableCell>Plan</TableCell>
                        <TableCell>Monthly price</TableCell>
                        <TableCell>Included selections</TableCell>
                        <TableCell>Services</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>Free</TableCell>
                        <TableCell>$0/month</TableCell>
                        <TableCell>10K / month</TableCell>
                        <TableCell>3</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Starter</TableCell>
                        <TableCell>$400/month</TableCell>
                        <TableCell>100K / month</TableCell>
                        <TableCell>5</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Team</TableCell>
                        <TableCell>$1,200/month</TableCell>
                        <TableCell>500K / month</TableCell>
                        <TableCell>10</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Business</TableCell>
                        <TableCell>$3,500/month</TableCell>
                        <TableCell>3M / month</TableCell>
                        <TableCell>30</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Enterprise</TableCell>
                        <TableCell>Custom</TableCell>
                        <TableCell>Custom</TableCell>
                        <TableCell>Unlimited</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Stack>
            </CardContent>
          </Card>
        )}

        <ActionButton
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/signup')}
          sx={{ alignSelf: 'flex-start' }}
        >
          Back to signup
        </ActionButton>
      </Stack>
    </Box>
  );
}
